import React, { useContext, useState } from 'react';
import styled from 'astroturf/react';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { Button } from 'js/design_system/Button';
import { CiterightContext } from './CiterightContextProvider';
import { SavedReferencesList } from './SavedReferencesList';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

interface SavedReferencesModalProps {
  /**
   * If true, renders the button as icon-only (no text) to save space.
   * Useful for constrained layouts like the sidebar view.
   */
  iconOnly?: boolean;
}

/**
 * A modal component that displays the saved references list.
 * The modal is triggered by a button that shows the count of saved references.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { SavedReferencesModal } from './SavedReferencesModal';
 *
 * // Full button with text
 * <SavedReferencesModal />
 *
 * // Icon-only button (for constrained spaces)
 * <SavedReferencesModal iconOnly />
 * ```
 */
export const SavedReferencesModal: React.FC<SavedReferencesModalProps> = ({
  iconOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const context = useContext(CiterightContext);

  if (!context) {
    return null;
  }

  const { savedReferences } = context;
  const savedCount = savedReferences.length;

  return (
    <>
      <ImpressionWatcher
        source="citeright.saved_references_button"
        entity_id={context.reportId || undefined}
      >
        {iconOnly ? (
          <IconButtonWrapper>
            <Button
              size="sm"
              icon="bookmark_add"
              onClick={() => setIsOpen(true)}
              title={`Saved References${savedCount > 0 ? ` (${savedCount})` : ''}`}
              aria-label={`Saved References${savedCount > 0 ? ` (${savedCount})` : ''}`}
            />
            {savedCount > 0 && (
              <IconBadge color="white">{savedCount}</IconBadge>
            )}
          </IconButtonWrapper>
        ) : (
          <Button size="sm" icon="bookmark_add" onClick={() => setIsOpen(true)}>
            Saved References
            {savedCount > 0 && <CountBadge>{savedCount}</CountBadge>}
          </Button>
        )}
      </ImpressionWatcher>

      <StyledModal
        title={`Saved References (${savedCount})`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        showHeader={true}
      >
        <ModalContent>
          <SavedReferencesList />
        </ModalContent>
      </StyledModal>
    </>
  );
};

const IconButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const IconBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: var(--brand-colors-blue-600);
  color: var(--background-white, #ffffff);
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border: 2px solid var(--background-white, #ffffff);
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  background: var(--background-white, #ffffff);
  color: var(--brand-colors-blue-600);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;

const StyledModal = styled(Modal)`
  .ds2-5-modal__container {
    max-width: 600px;
    width: 100%;
    max-height: 85vh;
    height: auto;
  }

  .ds2-5-modal__content {
    max-height: calc(85vh - 80px);
    overflow-y: auto;
  }
`;

export default SavedReferencesModal;
